if (!customElements.get("modal-dialog")) {
  class ModalDialog extends HTMLElement {
    constructor() {
      super();
      this.querySelector('[id^="ModalClose-"]')?.addEventListener(
        "click",
        this.hide.bind(this)
      );
      this.addEventListener("keyup", (event) => {
        if (event.code.toUpperCase() === "ESCAPE") this.hide();
      });
      if (this.classList.contains("media-modal")) {
        this.addEventListener("pointerup", (event) => {
          if (
            event.pointerType === "mouse" &&
            !event.target.closest("deferred-media, product-model, .td-product-gallery-zoom-modal__slider")
          )
            this.hide();
        });
      } else {
        this.addEventListener("click", (event) => {
          if (event.target.nodeName === "MODAL-DIALOG") this.hide();
        });
      }
    }
    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }
    show(opener) {
      this.openedBy = opener;
      const popup = this.querySelector(".template-popup");
      this.scrollY = window.scrollY;
      document.documentElement.style.scrollBehavior = 'auto';
      document.documentElement.classList.add("overflow-hidden");
      document.documentElement.scrollTop = this.scrollY;
      this.setAttribute("open", "");
      if (popup) popup.loadContent();
      if (typeof trapFocus === 'function') trapFocus(this, this.querySelector('[role="dialog"]'));

      this.dispatchEvent(new CustomEvent('show', {
        detail: { opener }
      }));

      // Auto-play video when modal opens
      const deferredMedia = this.querySelector('deferred-media');
      if (deferredMedia) {
        deferredMedia.loadContent(false);
        
        // Wait for video to be loaded before playing (especially important for Safari)
        const playVideoWhenReady = () => {
          const iframe = deferredMedia.querySelector('iframe[data-video-type]');
          const video = deferredMedia.querySelector('video');
          
          if (iframe) {
            // For iframe videos (YouTube/Vimeo), wait for iframe to load
            if (iframe.contentWindow) {
              // Small delay to ensure iframe is ready
              setTimeout(() => {
                if (iframe.dataset.videoType === 'youtube') {
                  iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                  iframe.contentWindow.postMessage('{"event":"command","func":"unMute","args":""}', '*');
                } else if (iframe.dataset.videoType === 'vimeo') {
                  iframe.contentWindow.postMessage('{"method":"play"}', '*');
                  iframe.contentWindow.postMessage('{"method":"setVolume","value":1}', '*');
                }
              }, 100);
            } else {
              // Retry if iframe not ready yet
              iframe.addEventListener('load', () => {
                setTimeout(() => {
                  if (iframe.dataset.videoType === 'youtube') {
                    iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                    iframe.contentWindow.postMessage('{"event":"command","func":"unMute","args":""}', '*');
                  } else if (iframe.dataset.videoType === 'vimeo') {
                    iframe.contentWindow.postMessage('{"method":"play"}', '*');
                    iframe.contentWindow.postMessage('{"method":"setVolume","value":1}', '*');
                  }
                }, 100);
              }, { once: true });
            }
          } else if (video) {
            // For HTML5 video elements
            const attemptPlay = () => {
              // Ensure video has playsinline attribute for Safari
              if (!video.hasAttribute('playsinline')) {
                video.setAttribute('playsinline', '');
              }
              
              // Hide poster button when video starts playing
              const posterButton = deferredMedia.querySelector('.deferred-media__poster-button');
              if (posterButton) {
                video.addEventListener('playing', () => {
                  posterButton.classList.add('deferred-media__playing');
                }, { once: true });
              }
              
              // Safari requires muted to be false only after user interaction
              // But we can try to play first, then unmute
              const playPromise = video.play();
              
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    // Video started playing, now unmute
                    video.muted = false;
                    // Update deferred media state
                    deferredMedia.isPlaying = true;
                    if (deferredMedia.updatePlayPauseHint) {
                      deferredMedia.updatePlayPauseHint(true);
                    }
                  })
                  .catch((error) => {
                    // If play fails, try with muted first (Safari fallback)
                    video.muted = true;
                    const mutedPlayPromise = video.play();
                    if (mutedPlayPromise !== undefined) {
                      mutedPlayPromise
                        .then(() => {
                          // After successful muted play, try to unmute
                          setTimeout(() => {
                            video.muted = false;
                            // Update deferred media state
                            deferredMedia.isPlaying = true;
                            if (deferredMedia.updatePlayPauseHint) {
                              deferredMedia.updatePlayPauseHint(true);
                            }
                          }, 100);
                        })
                        .catch((err) => {
                          console.warn('Video autoplay failed:', err);
                          // If still fails, user will need to click play button
                        });
                    }
                  });
              }
            };
            
            // Wait for video to be ready
            if (video.readyState >= 2) {
              // Video already loaded
              attemptPlay();
            } else {
              // Wait for video to load
              video.addEventListener('loadeddata', attemptPlay, { once: true });
              video.addEventListener('canplay', attemptPlay, { once: true });
              
              // Fallback timeout
              setTimeout(attemptPlay, 500);
            }
          } else if (deferredMedia.playMedia) {
            // Fallback to playMedia method
            setTimeout(() => {
              deferredMedia.playMedia();
            }, 200);
          }
        };
        
        // Wait a bit for DOM to update after loadContent
        setTimeout(playVideoWhenReady, 50);
      }
    }
    hide() {
      document.documentElement.classList.remove("overflow-hidden");
      document.documentElement.scrollTop = this.scrollY;
      document.documentElement.style.scrollBehavior = '';
      this.removeAttribute("open");

      // Pause and reset video when modal closes
      const deferredMedia = this.querySelector('deferred-media');
      if (deferredMedia) {
        if (deferredMedia.pauseMedia) deferredMedia.pauseMedia();
        const video = this.querySelector('video');
        if (video) {
          video.currentTime = 0;
        }
        const iframe = this.querySelector('iframe[data-video-type]');
        if (iframe && iframe.contentWindow) {
          // For YouTube videos, seek to beginning
          if (iframe.dataset.videoType === 'youtube') {
            iframe.contentWindow.postMessage('{"event":"command","func":"seekTo","args":[0,true]}', '*');
          }
        }
      }

      if (this.openedBy && typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
    }
  }
  customElements.define("modal-dialog", ModalDialog);
}

if (!customElements.get("modal-opener")) {
  class ModalOpener extends HTMLElement {
    constructor() {
      super();
      this.addEventListener("click", () => {
        const modal = document.querySelector(`#${this.getAttribute("data-modal")}`);
        if (!modal) return;

        // Inline mode: desktop + data-inline-desktop attribute
        (window.innerWidth >= 1024 && this.dataset.inlineDesktop === 'true')
          ? this.playInline(modal)
          : modal.show(this);
      });
    }

    playInline(modal) {
      const deferredMedia = modal.querySelector('deferred-media');
      if (!deferredMedia) return;

      this.style.display = 'none';
      const videoClone = deferredMedia.cloneNode(true);
      videoClone.classList.add('td-inline-video-player');
      this.parentNode.insertBefore(videoClone, this.nextSibling);

      setTimeout(() => {
        videoClone.loadContent(false);
        const video = videoClone.querySelector('video');
        if (!video) return;

        video.muted = false;
        video.play();

        const resetThumbnail = () => (videoClone.remove(), this.style.display = '');
        video.addEventListener('pause', () => !video.ended && resetThumbnail());
        video.addEventListener('ended', resetThumbnail);
      }, 100);
    }
  }
  customElements.define("modal-opener", ModalOpener);
}
  
const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}